import { Download, Calendar, User, FileText, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

interface SixMWTReport {
  id: string;
  testDate: string;
  week: number;
  patientInfo: {
    name: string;
    umrNo: string;
    age: number;
    gender: 'Male' | 'Female';
    height: number; // cm
    weight: number; // kg
    bmi: number;
  };
  vitals: {
    heartRate: {
      baseline: number;
      duringTest: number;
      postTest: number;
    };
    bloodPressure: {
      baseline: { systolic: number; diastolic: number };
      duringTest?: { systolic: number; diastolic: number };
      postTest: { systolic: number; diastolic: number };
    };
    saturation: {
      baseline: number;
      duringTest: number;
      postTest: number;
    };
    borgScale: {
      baseline: string;
      duringTest: string;
      postTest: string;
    };
  };
  results: {
    predictedDistance: number; // meters
    lowerLimitNormal: number; // meters
    actualDistance: number; // meters
    predictedPercentage: number; // percentage
  };
  sppbScore?: string;
  gripStrength?: {
    right: number;
    left: number;
  };
  physiotherapist: {
    name: string;
    credentials: string;
  };
  status: 'pending' | 'reviewed' | 'approved';
  notes?: string;
}

interface SixMWTReportViewProps {
  patientId: string;
  patientName: string;
  reports: SixMWTReport[];
  onApprove?: (reportId: string) => void;
  onReject?: (reportId: string) => void;
}

export function SixMWTReportView({ patientId, patientName, reports, onApprove, onReject }: SixMWTReportViewProps) {
  const handleDownload = (report: SixMWTReport) => {
    toast.success('Downloading report...', {
      description: `${report.patientInfo.name} - Week ${report.week}`,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'pending':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'reviewed':
        return 'bg-primary/10 text-primary border-primary';
      default:
        return '';
    }
  };

  const calculateChange = (current: number, previous: number | undefined) => {
    if (!previous) return null;
    const change = current - previous;
    const percentage = ((change / previous) * 100).toFixed(1);
    return { change, percentage };
  };

  return (
    <div className="space-y-6">
      {reports.map((report, index) => {
        const previousReport = index > 0 ? reports[index - 1] : undefined;
        const distanceChange = calculateChange(
          report.results.actualDistance,
          previousReport?.results.actualDistance
        );
        const percentageChange = calculateChange(
          report.results.predictedPercentage,
          previousReport?.results.predictedPercentage
        );

        return (
          <Card key={report.id} className="shadow-lg">
            <CardHeader className="bg-white border-b border-border">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                    <CardTitle className="text-primary text-base sm:text-lg">SIX MINUTE WALK TEST REPORT</CardTitle>
                    <Badge variant="outline" className={getStatusColor(report.status)}>
                      {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-muted-foreground text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Week {report.week}</span>
                    </div>
                    <Separator orientation="vertical" className="h-4 hidden sm:block" />
                    <span>Date: {new Date(report.testDate).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleDownload(report)} className="self-start">
                  <Download className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Download PDF</span>
                  <span className="sm:hidden">Download</span>
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Patient Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">PATIENT NAME:</span>
                    <span className="text-foreground">{report.patientInfo.name}</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">UMR NO:</span>
                    <span className="text-foreground">{report.patientInfo.umrNo}</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">AGE:</span>
                    <span className="text-foreground">{report.patientInfo.age} Yrs</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">GENDER:</span>
                    <span className="text-foreground">{report.patientInfo.gender}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">DATE:</span>
                    <span className="text-foreground">{new Date(report.testDate).toLocaleDateString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">HEIGHT:</span>
                    <span className="text-foreground">{report.patientInfo.height}cm</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">WEIGHT:</span>
                    <span className="text-foreground">{report.patientInfo.weight.toFixed(1)}kg</span>
                  </div>
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">BMI:</span>
                    <span className="text-foreground">{report.patientInfo.bmi.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* Vitals Table */}
              <div>
                <h3 className="text-primary mb-3">Vitals Parameters</h3>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                    <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary">
                        <TableHead className="font-bold text-foreground">PARAMETERS</TableHead>
                        <TableHead className="font-bold text-foreground text-center">BASELINE</TableHead>
                        <TableHead className="font-bold text-foreground text-center">DURING THE TEST</TableHead>
                        <TableHead className="font-bold text-foreground text-center">POST TEST</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">HEART RATE (Beats/min)</TableCell>
                        <TableCell className="text-center">{report.vitals.heartRate.baseline}</TableCell>
                        <TableCell className="text-center bg-[#FEEBDA]">
                          {report.vitals.heartRate.duringTest}
                        </TableCell>
                        <TableCell className="text-center">{report.vitals.heartRate.postTest}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">BLOOD PRESSURE (mmHg)</TableCell>
                        <TableCell className="text-center">
                          {report.vitals.bloodPressure.baseline.systolic}/
                          {report.vitals.bloodPressure.baseline.diastolic}
                        </TableCell>
                        <TableCell className="text-center bg-[#FEEBDA]">
                          {report.vitals.bloodPressure.duringTest 
                            ? `${report.vitals.bloodPressure.duringTest.systolic}/${report.vitals.bloodPressure.duringTest.diastolic}`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {report.vitals.bloodPressure.postTest.systolic}/
                          {report.vitals.bloodPressure.postTest.diastolic}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SATURATION (%)</TableCell>
                        <TableCell className="text-center">
                          {report.vitals.saturation.baseline}% ON ROOM AIR
                        </TableCell>
                        <TableCell className="text-center bg-[#FEEBDA]">
                          {report.vitals.saturation.duringTest}% ON ROOM AIR
                        </TableCell>
                        <TableCell className="text-center">
                          {report.vitals.saturation.postTest}% ON ROOM AIR
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">BORG SCALE</TableCell>
                        <TableCell className="text-center">{report.vitals.borgScale.baseline}</TableCell>
                        <TableCell className="text-center bg-[#FEEBDA]">
                          {report.vitals.borgScale.duringTest}
                        </TableCell>
                        <TableCell className="text-center">{report.vitals.borgScale.postTest}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </div>
              </div>

              {/* Results Table */}
              <div>
                <h3 className="text-primary mb-3">RESULT</h3>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                    <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary">
                        <TableHead className="font-bold text-foreground text-center">
                          PREDICTED DISTANCE IN METRES
                        </TableHead>
                        <TableHead className="font-bold text-foreground text-center">
                          LOWER LIMIT OF NORMAL
                        </TableHead>
                        <TableHead className="font-bold text-foreground text-center">
                          ACTUAL DISTANCE IN METRES
                        </TableHead>
                        <TableHead className="font-bold text-foreground text-center">
                          PREDICTED PERCENTAGE (%)
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-center text-primary">
                          {report.results.predictedDistance}mts
                        </TableCell>
                        <TableCell className="text-center">{report.results.lowerLimitNormal}mts</TableCell>
                        <TableCell className="text-center bg-[#D4F0F8] text-primary">
                          {report.results.actualDistance}mts
                        </TableCell>
                        <TableCell className="text-center text-primary">
                          {report.results.predictedPercentage.toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </div>
              </div>

              {/* Trend Comparison */}
              {(distanceChange || percentageChange) && (
                <div className="p-3 sm:p-4 bg-[#D4F0F8] rounded-xl">
                  <h4 className="text-primary mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Activity className="w-4 h-4" />
                    Comparison with Previous Week (Week {report.week - 1})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {distanceChange && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <span className="text-muted-foreground">Distance Change:</span>
                        <div className="flex items-center gap-2">
                          {distanceChange.change >= 0 ? (
                            <TrendingUp className="w-4 h-4 text-[#14CAF4]" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-destructive" />
                          )}
                          <span className={distanceChange.change >= 0 ? 'text-[#14CAF4]' : 'text-destructive'}>
                            {distanceChange.change >= 0 ? '+' : ''}
                            {distanceChange.change.toFixed(0)}m ({distanceChange.percentage}%)
                          </span>
                        </div>
                      </div>
                    )}
                    {percentageChange && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <span className="text-muted-foreground">% Predicted Change:</span>
                        <div className="flex items-center gap-2">
                          {percentageChange.change >= 0 ? (
                            <TrendingUp className="w-4 h-4 text-[#14CAF4]" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-destructive" />
                          )}
                          <span className={percentageChange.change >= 0 ? 'text-[#14CAF4]' : 'text-destructive'}>
                            {percentageChange.change >= 0 ? '+' : ''}
                            {percentageChange.change.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Assessments */}
              {(report.sppbScore || report.gripStrength) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {report.sppbScore && (
                    <div className="p-4 bg-secondary rounded-xl">
                      <h4 className="text-primary mb-2">SPPB SCORE:</h4>
                      <p className="text-foreground">{report.sppbScore}</p>
                    </div>
                  )}
                  {report.gripStrength && (
                    <div className="p-4 bg-secondary rounded-xl">
                      <h4 className="text-primary mb-2">GRIP STRENGTH:</h4>
                      <p className="text-foreground">
                        RIGHT - {report.gripStrength.right} &nbsp;&nbsp; LEFT - {report.gripStrength.left}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {report.notes && (
                <div className="p-4 bg-[#FEEBDA] rounded-xl">
                  <h4 className="text-primary mb-2">Clinical Notes:</h4>
                  <p className="text-foreground">{report.notes}</p>
                </div>
              )}

              {/* Physiotherapist Info */}
              <div className="border-t border-border pt-4">
                <h4 className="text-primary mb-2">PHYSIOTHERAPIST</h4>
                <p className="text-foreground">{report.physiotherapist.name}</p>
                <p className="text-muted-foreground">{report.physiotherapist.credentials}</p>
              </div>

              {/* Approval Actions */}
              {report.status === 'pending' && onApprove && onReject && (
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                  <Button
                    className="flex-1 bg-[#14CAF4] hover:bg-[#14CAF4]/90"
                    onClick={() => onApprove(report.id)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Approve Report
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => onReject(report.id)}
                  >
                    Request Re-assessment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {reports.length === 0 && (
        <Card className="shadow-lg">
          <CardContent className="p-8 sm:p-12 text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-primary mb-2">No 6MWT Reports Available</h3>
            <p className="text-muted-foreground">
              No 6-Minute Walk Test reports have been recorded for this patient yet.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
